import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Receipt, Search, FileText, Bus as BusIcon, Edit2, Eye, ArrowRight } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const ExpenseList = () => {
    const { data: expensesResponse, refresh, loading } = useGet('/expenses');
    const expenses = Array.isArray(expensesResponse) ? expensesResponse : (expensesResponse?.data || []);
    
    const { data: expenseTypes } = useGet('/expense-types');
    const { data: suppliersResponse } = useGet('/suppliers');
    const { data: busesResponse } = useGet('/buses?all=true');
    const { data: paymentModesResponse } = useGet('/payment-modes');
    const suppliers = Array.isArray(suppliersResponse) ? suppliersResponse : (suppliersResponse?.data || []);
    const buses = Array.isArray(busesResponse) ? busesResponse : (busesResponse?.data || []);
    const paymentModes = Array.isArray(paymentModesResponse) ? paymentModesResponse : (paymentModesResponse?.data || []);

    const { hasPermission, selectedYear } = useAuth();
    const canCreate = hasPermission('payments', 'create');
    const canDelete = hasPermission('payments', 'delete');
    const canTransfer = hasPermission('payments', 'transfer_expense');
    
    const { success, error: toastError } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);

    const especeModeId = paymentModes.find(m => m.name === 'ESPECE')?.id;
    
    // Calculate total expenses amount
    const totalExpensesAmount = expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const paidCashAmount = expenses
        .filter(exp => exp.bank_transaction_id && exp.bank_transaction?.payment_mode_id == especeModeId)
        .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const paidBankAmount = expenses
        .filter(exp => (exp.bank_transaction_id && exp.bank_transaction?.payment_mode_id != especeModeId) || (exp.is_paid && !exp.bank_transaction_id))
        .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const unpaidAmount = expenses
        .filter(exp => !exp.bank_transaction_id && exp.is_paid !== true && exp.is_paid !== 1 && exp.is_paid !== '1')
        .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

    // Form State
    const [formData, setFormData] = useState({
        transaction_date: new Date().toISOString().split('T')[0],
        expense_type_id: '',
        supplier_id: '',
        amount: '',
        invoice_number: '',
        buses: []
    });

    const selectedType = Array.isArray(expenseTypes) ? expenseTypes.find(t => t.id.toString() === formData.expense_type_id?.toString()) : null;
    const isBusRelated = selectedType && selectedType.category === 'Materiel';

    const handleBusToggle = (busId) => {
        setFormData(prev => {
            const currentBuses = [...prev.buses];
            const existingIndex = currentBuses.findIndex(b => b.bus_id === busId);
            if (existingIndex >= 0) {
                currentBuses.splice(existingIndex, 1);
            } else {
                currentBuses.push({ bus_id: busId, amount: '' });
            }
            const total = currentBuses.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
            return { ...prev, buses: currentBuses, amount: total || '' };
        });
    };

    const handleBusAmountChange = (busId, val) => {
        setFormData(prev => {
            const currentBuses = prev.buses.map(b => b.bus_id === busId ? { ...b, amount: val } : b);
            const total = currentBuses.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
            return { ...prev, buses: currentBuses, amount: total || '' };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;
        setIsSubmitting(true);
        try {
            const payload = { ...formData };
            if (!isBusRelated) {
                delete payload.buses;
            }
            if (editingExpense) {
                await api.put(`/expenses/${editingExpense.id}`, payload);
                success('تم تعديل المصروف بنجاح');
            } else {
                await api.post('/expenses', payload);
                success('تم تسجيل المصروف بنجاح');
            }
            setIsModalOpen(false);
            setEditingExpense(null);
            setIsReadOnly(false);
            setFormData({
                transaction_date: new Date().toISOString().split('T')[0],
                expense_type_id: '',
                supplier_id: '',
                amount: '',
                invoice_number: '',
                buses: []
            });
            refresh();
        } catch (err) { 
            toastError('خطأ أثناء الحفظ'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (expense) => {
        setEditingExpense(expense);
        setIsReadOnly(false);
        setFormData({
            transaction_date: expense.transaction_date || new Date().toISOString().split('T')[0],
            expense_type_id: expense.expense_type_id || '',
            supplier_id: expense.supplier_id || '',
            amount: expense.amount || '',
            invoice_number: expense.invoice_number || '',
            buses: expense.buses ? expense.buses.map(b => ({ bus_id: b.bus_id || b.id, amount: b.amount || b.pivot?.amount || '' })) : []
        });
        setIsModalOpen(true);
    };

    const handleViewClick = (expense) => {
        setEditingExpense(expense);
        setIsReadOnly(true);
        setFormData({
            transaction_date: expense.transaction_date || new Date().toISOString().split('T')[0],
            expense_type_id: expense.expense_type_id || '',
            supplier_id: expense.supplier_id || '',
            amount: expense.amount || '',
            invoice_number: expense.invoice_number || '',
            buses: expense.buses ? expense.buses.map(b => ({ bus_id: b.bus_id || b.id, amount: b.amount || b.pivot?.amount || '' })) : []
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingExpense(null);
        setIsReadOnly(false);
        setFormData({
            transaction_date: new Date().toISOString().split('T')[0],
            expense_type_id: '',
            supplier_id: '',
            amount: '',
            invoice_number: '',
            buses: []
        });
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف هذا المصروف؟')) {
            try {
                await api.delete(`/expenses/${id}`);
                success('تم الحذف بنجاح');
                refresh();
            } catch (err) {
                toastError(err?.response?.data?.message || 'خطأ أثناء الحذف');
            }
        }
    };

    const handleTransfer = async (id) => {
        if(confirm('هل أنت متأكد من تحويل هذا المصروف غير المدفوع إلى السنة الدراسية الحالية (لإعادة جدولته)؟')) {
            try {
                await api.put(`/expenses/${id}/transfer`);
                success('تم تحويل المصروف بنجاح');
                refresh();
            } catch (err) {
                toastError(err?.response?.data?.message || 'خطأ أثناء التحويل');
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800">إدارة المصروفات</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">تتبع وتسجيل جميع المصاريف والفواتير</p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                    {canCreate && (
                        <Button onClick={() => {
                            setEditingExpense(null);
                            setIsReadOnly(false);
                            setFormData({
                                transaction_date: new Date().toISOString().split('T')[0],
                                expense_type_id: '',
                                supplier_id: '',
                                amount: '',
                                invoice_number: '',
                                reglement_date: '',
                                piece_number: '',
                                payment_mode_id: '',
                                buses: []
                            });
                            setIsModalOpen(true);
                        }} className="flex items-center gap-2">
                            <Plus className="w-4 h-4" /> تسجيل مصروف
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow">
                    <span className="text-xs font-bold text-slate-400 uppercase">إجمالي المصاريف</span>
                    <span className="text-lg font-bold text-slate-800">{totalExpensesAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow">
                    <span className="text-xs font-bold text-slate-400 uppercase">المدفوعة نقداً</span>
                    <span className="text-lg font-bold text-emerald-600">{paidCashAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow">
                    <span className="text-xs font-bold text-slate-400 uppercase">المدفوعة بنكياً</span>
                    <span className="text-lg font-bold text-blue-600">{paidBankAmount.toFixed(2)} د.م</span>
                </Card>
                <Card className="p-4 border-slate-100 flex flex-col gap-1 hover:shadow-md transition-shadow">
                    <span className="text-xs font-bold text-slate-400 uppercase">الباقي (غير مدفوع)</span>
                    <span className="text-lg font-bold text-amber-600">{unpaidAmount.toFixed(2)} د.م</span>
                </Card>
            </div>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                        <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">تاريخ العملية</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">نوع المصروف</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">المورد</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">مبلغ الفاتورة</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">رقم الفاتورة</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">الحالة</th>
                                <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading && (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-slate-400 animate-pulse">جاري التحميل...</td>
                                </tr>
                            )}
                            {!loading && expenses?.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-slate-400">لا توجد مصاريف مسجلة حالياً</td>
                                </tr>
                            )}
                            {expenses?.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 text-slate-800 text-sm">{item.transaction_date}</td>
                                    <td className="px-6 py-4">
                                        <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs font-medium">
                                            {item.expense_type?.name || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600 text-sm">{item.supplier?.name || '-'}</td>
                                    <td className="px-6 py-4 text-emerald-600 font-bold">{parseFloat(item.amount).toFixed(2)} د.م</td>
                                    <td className="px-6 py-4 text-slate-500 text-sm">{item.invoice_number || '-'}</td>
                                    <td className="px-6 py-4">
                                        {(item.bank_transaction_id || item.is_paid === true || item.is_paid === 1 || item.is_paid === '1') ? (
                                            <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-1 rounded border border-primary/20">تم الدفع</span>
                                        ) : (
                                            <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">في الانتظار</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-left">
                                        <div className="flex items-center justify-end gap-2">
                                            {!!(item.bank_transaction_id || item.is_paid === true || item.is_paid === 1 || item.is_paid === '1') && (
                                                <button 
                                                    onClick={() => handleViewClick(item)} 
                                                    className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                                    title="معاينة"
                                                >
                                                    <Eye size={16}/>
                                                </button>
                                            )}
                                            {canTransfer && !item.bank_transaction_id && (item.is_paid !== true && item.is_paid !== 1 && item.is_paid !== '1') && selectedYear && item.transaction_date < selectedYear.start_date && (
                                                <button 
                                                    onClick={() => handleTransfer(item.id)} 
                                                    className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                                                    title="ترحيل إلى السنة الحالية"
                                                >
                                                    <ArrowRight size={16}/>
                                                </button>
                                            )}
                                            {canCreate && !item.bank_transaction_id && (item.is_paid !== true && item.is_paid !== 1 && item.is_paid !== '1') && (
                                                <button 
                                                    onClick={() => handleEditClick(item)} 
                                                    className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                                    title="تعديل"
                                                >
                                                    <Edit2 size={16}/>
                                                </button>
                                            )}
                                            {!item.bank_transaction_id && (item.is_paid !== true && item.is_paid !== 1 && item.is_paid !== '1') && canDelete && (
                                                <button 
                                                    onClick={() => handleDelete(item.id)} 
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={isReadOnly ? "معاينة المصروف" : (editingExpense ? "تعديل المصروف" : "تسجيل مصروف جديد")}>
                <form onSubmit={handleAdd} className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input 
                            label="تاريخ العملية" 
                            type="date"
                            name="transaction_date"
                            value={formData.transaction_date} 
                            onChange={handleChange} 
                            required 
                            disabled={isReadOnly}
                        />
                        <Input 
                            label="مبلغ الفاتورة" 
                            type="number"
                            step="0.01"
                            name="amount"
                            value={formData.amount} 
                            onChange={handleChange} 
                            placeholder="مثال: 500.00"
                            required 
                            disabled={isReadOnly || isBusRelated}
                            className={isBusRelated ? "bg-slate-50 cursor-not-allowed" : ""}
                        />
                        
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-slate-700">نوع المصروف</label>
                            <select 
                                name="expense_type_id"
                                value={formData.expense_type_id}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none disabled:bg-slate-50"
                                required
                                disabled={isReadOnly}
                            >
                                <option value="">-- اختر نوع المصروف --</option>
                                {Array.isArray(expenseTypes) && expenseTypes.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-slate-700">اسم المورد</label>
                            <select 
                                name="supplier_id"
                                value={formData.supplier_id}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none disabled:bg-slate-50"
                                disabled={isReadOnly}
                            >
                                <option value="">-- اختر المورد (اختياري) --</option>
                                {suppliers.map(sup => (
                                    <option key={sup.id} value={sup.id}>{sup.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <Input 
                                label="رقم الفاتورة" 
                                name="invoice_number"
                                value={formData.invoice_number} 
                                onChange={handleChange} 
                                placeholder="مثال: INV-2026-001"
                                disabled={isReadOnly}
                            />
                        </div>



                        {isBusRelated && (
                            <div className="md:col-span-2 space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                    <BusIcon size={16} className="text-primary" />
                                    توزيع المصروف على الحافلات
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
                                    {buses.map(bus => {
                                        const busData = formData.buses.find(b => b.bus_id === bus.id);
                                        const isSelected = !!busData;
                                        return (
                                            <div key={bus.id} className={`flex flex-col gap-2 p-3 border rounded-lg transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => !isReadOnly && handleBusToggle(bus.id)}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isSelected}
                                                        onChange={() => {}}
                                                        disabled={isReadOnly}
                                                        className="w-4 h-4 text-primary rounded border-slate-300 focus:ring-primary disabled:opacity-50"
                                                    />
                                                    <span className="text-sm font-bold text-slate-700">{bus.matricule} ({bus.code})</span>
                                                </div>
                                                {isSelected && (
                                                    <div className="pr-6">
                                                        <input 
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="المبلغ المصروف"
                                                            value={busData.amount}
                                                            onChange={(e) => handleBusAmountChange(bus.id, e.target.value)}
                                                            className="w-full text-sm p-1.5 border border-primary/30 rounded focus:outline-none focus:border-primary disabled:bg-slate-50"
                                                            required
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {buses.length === 0 && <p className="text-xs text-slate-500">لا توجد حافلات مسجلة في النظام.</p>}
                            </div>
                        )}
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
                        <Button type="button" variant={isReadOnly ? "primary" : "outline"} onClick={handleCloseModal}>
                            {isReadOnly ? "إغلاق" : "إلغاء"}
                        </Button>
                        {!isReadOnly && (
                            <Button type="submit" isLoading={isSubmitting}>
                                حفظ المصروف
                            </Button>
                        )}
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ExpenseList;
